import type { PaginationState } from '../hooks/usePagination';
import {
  type PaginatedResponse,
  type PaginationParams,
} from '../services/types/pagination-types';

/**
 * Safely parse a URL param as a number with fallback.
 */
export function parseNumberParam(
  value: string | null,
  fallback: number,
): number {
  if (!value) return fallback;
  const parsed = Number(value);
  return Number.isNaN(parsed) || parsed < 0 ? fallback : parsed;
}

/**
 * Extract results array from paginated response.
 */
export function getResults<T>(data: PaginatedResponse<T> | undefined): T[] {
  return data?.results ?? [];
}

/**
 * Get total count from paginated response.
 */
export function getTotalCount<T>(
  data: PaginatedResponse<T> | undefined,
): number {
  return data?.count ?? 0;
}

/**
 * Convert usePagination state to API query params.
 *
 * Generic helper that works for any paginated endpoint (Intakes, Matters, Clients, etc.)
 * by flattening the nested filter structure into flat query params.
 *
 * Includes search parameter for backend filtering via Django's SearchFilter.
 *
 * @example
 * // Intakes page
 * const params = toQueryParams<IntakeFilterParams>(state);
 * useGetIntakesQuery(params); // → GET /api/intakes/?limit=50&offset=0&search=John
 *
 * // Matters page
 * const params = toQueryParams<MatterFilterParams>(state);
 * useGetMattersQuery(params); // → GET /api/matters/?limit=50&offset=0&search=Smith
 */
export function toQueryParams<T extends PaginationParams>(
  state: PaginationState,
): Partial<T> {
  return {
    limit: state.limit,
    offset: state.offset,
    ordering: state.ordering || undefined,
    search: state.search || undefined,
    ...state.filters,
  } as Partial<T>;
}

/**
 * Helper to ensure value is an array (or undefined if falsy)
 * Useful for standardizing filter params that can be single string or array.
 */
export function ensureArray(
  value: string | string[] | undefined | null,
): string[] | undefined {
  if (!value) return undefined;
  return Array.isArray(value) ? value : [value];
}
